import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Languages, X } from "lucide-react";
import { setLanguage } from "@/i18n";

const SOURCE_LANGUAGE = "en";
const DISMISS_KEY_PREFIX = "bac.autoTranslateDisclosure.dismissed.";

type Variant = "general" | "legal";

interface AutoTranslationDisclosureProps {
  variant?: Variant;
  className?: string;
}

function getCurrentLang(resolved: string | undefined, language: string | undefined): string {
  return (resolved || language || SOURCE_LANGUAGE).slice(0, 2).toLowerCase();
}

export function AutoTranslationDisclosure({ variant = "general", className }: AutoTranslationDisclosureProps) {
  const { t, i18n } = useTranslation();
  const lang = getCurrentLang(i18n.resolvedLanguage, i18n.language);
  const storageKey = `${DISMISS_KEY_PREFIX}${lang}.${variant}`;

  // The "legal" variant must NEVER be permanently dismissed: the disclaimer
  // (the English version is the legally binding one) needs to be re-shown on
  // every page navigation. The "general" variant can be remembered across
  // visits via localStorage.
  const isLegal = variant === "legal";

  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (isLegal) return false;
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem(storageKey) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (isLegal) {
      // Reset on every mount / language switch — the legal disclaimer must
      // reappear on the next page load even after the user closed it.
      setDismissed(false);
      return;
    }
    if (typeof window === "undefined") return;
    try {
      setDismissed(window.localStorage.getItem(storageKey) === "1");
    } catch {
      setDismissed(false);
    }
  }, [storageKey, isLegal]);

  if (lang === SOURCE_LANGUAGE) return null;
  if (dismissed) return null;

  const dismiss = () => {
    if (!isLegal) {
      try {
        if (typeof window !== "undefined") {
          window.localStorage.setItem(storageKey, "1");
        }
      } catch {
        // ignore storage errors
      }
    }
    setDismissed(true);
  };

  const switchToEnglish = () => {
    setLanguage(SOURCE_LANGUAGE);
  };

  const titleKey = isLegal ? "autoTranslation.legal_title" : "autoTranslation.title";
  const bodyKey = isLegal ? "autoTranslation.legal_body" : "autoTranslation.body";
  const switchLabel = t("autoTranslation.view_in_english");
  const dismissLabel = t("autoTranslation.dismiss");
  const closeLabel = t("common.close", { defaultValue: "Close" });

  const containerClass = [
    isLegal
      ? "border-amber-300 bg-amber-50 text-amber-950"
      : "border-blue-200 bg-blue-50 text-blue-950",
    "relative flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-start sm:gap-4",
    className || "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      role={isLegal ? "alert" : "status"}
      aria-live="polite"
      className={containerClass}
      data-testid={`auto-translation-disclosure-${variant}`}
    >
      <div className="flex shrink-0 items-start gap-3 sm:items-center">
        <Languages
          className={isLegal ? "h-5 w-5 text-amber-700" : "h-5 w-5 text-blue-700"}
          strokeWidth={2}
          aria-hidden="true"
        />
      </div>
      <div className="min-w-0 flex-1 pr-7 sm:pr-8">
        <p className={isLegal ? "text-sm font-semibold" : "text-sm font-semibold"}>
          {t(titleKey)}
        </p>
        <p className="mt-1 text-sm leading-snug">{t(bodyKey)}</p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={switchToEnglish}
            className={
              isLegal
                ? "inline-flex items-center justify-center rounded-md bg-amber-900 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-amber-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-700"
                : "inline-flex items-center justify-center rounded-md bg-blue-700 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-blue-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            }
            data-testid={`button-auto-translation-view-en-${variant}`}
          >
            {switchLabel}
          </button>
          <button
            type="button"
            onClick={dismiss}
            className={
              isLegal
                ? "inline-flex items-center justify-center rounded-md px-3 py-1.5 text-xs font-medium text-amber-900 transition-colors hover:bg-amber-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-700"
                : "inline-flex items-center justify-center rounded-md px-3 py-1.5 text-xs font-medium text-blue-900 transition-colors hover:bg-blue-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            }
            data-testid={`button-auto-translation-dismiss-${variant}`}
          >
            {dismissLabel}
          </button>
        </div>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label={closeLabel}
        className={
          isLegal
            ? "absolute right-2 top-2 rounded-full p-1.5 text-amber-900 transition-colors hover:bg-amber-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-700"
            : "absolute right-2 top-2 rounded-full p-1.5 text-blue-900 transition-colors hover:bg-blue-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        }
        data-testid={`button-auto-translation-close-${variant}`}
      >
        <X className="h-4 w-4" strokeWidth={2} />
      </button>
    </div>
  );
}
